import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { DropdownItem } from 'reactstrap';
import { useDispatch, useSelector } from 'src/store/store';
import i18n from 'src/utils/translate';
import { trackFootfallEvent, trackFootfallEventWithHrefDelay } from 'src/utils/footfall';
import { Event, Section } from 'src/constants/footfall';
import { fetchConnections } from 'src/modules/user/profile/actions';
import Followers from 'src/modules/user/profile/components/profileFollowers';
import Followings from 'src/modules/user/profile/components/profileFollowings';
import ButtonDropdown from 'src/components/buttons/buttonDropdown';
import ShowMoreResults from 'src/modules/user/profile/components/shared/showMoreResults';
import { Connections } from 'src/constants/types';
import Loader from 'src/components/loader';
export default function ProfileConnections() {
  const dispatch = useDispatch();
  const [currentFilter, setCurrentFilter] = useState<string | Connections>(Connections.Followers);
  const followedUserIds = useSelector(({
    context
  }) => context.currentUser?.followedUserIds);
  const {
    profileUser,
    followers,
    followings,
    isLoading
  } = useSelector(({
    pages
  }) => pages.profile);
  const {
    isOwnProfile,
    isDeceased,
    isUsersImFollowingPublic
  } = profileUser;
  const dropDownOptions = {
    [Connections.Followers]: i18n.t('Followers'),
    ...(!isDeceased && (isOwnProfile || isUsersImFollowingPublic) && {
      [Connections.Followings]: i18n.t('Following')
    }) // plural on purpose - it's the exact api endpoint link.
  };
  const clickOnMemberCard = (href: string, username: string) => {
    trackFootfallEventWithHrefDelay(href, Event.Clicked, {
      clickType: `clicked-by-${username === profileUser.username ? `owner` : `other`}`,
      clickMetadata: {
        section: `member-card-profile-${currentFilter}`
      }
    });
  };
  const onShowMoreClick = (pageNumber: number) => {
    trackFootfallEvent(Event.Clicked, {
      clickType: 'show-more',
      clickMetadata: {
        section: currentFilter,
        origin: isOwnProfile ? 'own-profile' : 'other-profile'
      }
    });
    dispatch(fetchConnections(profileUser.userId, currentFilter, pageNumber));
  };
  useEffect(() => {
    if (!followedUserIds) return;
    dispatch(fetchConnections(profileUser.userId, currentFilter));
  }, [currentFilter, followedUserIds, dispatch, profileUser.userId]);
  return <PanelWrapper data-sentry-element="PanelWrapper" data-sentry-component="ProfileConnections" data-sentry-source-file="profileConnections.tsx">
      <StyledDropDown color="none" dataTestId="connections-dropdown" text={dropDownOptions[currentFilter]} data-sentry-element="StyledDropDown" data-sentry-source-file="profileConnections.tsx">
        {Object.keys(dropDownOptions).map(filter => <DropdownItem data-testid="connections-dropdown-options" disabled={filter === Connections.Followings && (isDeceased || !isOwnProfile && !isUsersImFollowingPublic)} key={filter} onClick={() => {
        setCurrentFilter(filter);
        trackFootfallEvent(Event.Clicked, {
          clickType: `selected-${filter}`,
          clickMetadata: {
            section: Section.FollowersFollowings,
            element: 'dropdown',
            origin: isOwnProfile ? 'own-profile' : 'other-profile'
          }
        });
      }}>
            {dropDownOptions[filter]}
          </DropdownItem>)}
      </StyledDropDown>

      {isLoading ? <Loader /> : <ConnectionsContainer data-testid="profile__connections_container">
            {{
        [Connections.Followers]: <Followers clickOnCard={clickOnMemberCard} />,
        [Connections.Followings]: <Followings clickOnCard={clickOnMemberCard} />
      }[currentFilter]}
            <ShowMoreResults items={currentFilter === Connections.Followers ? followers : followings} label={i18n.t('Show more')} onShowMore={onShowMoreClick} />
          </ConnectionsContainer>}
    </PanelWrapper>;
}
const PanelWrapper = styled.div`
  @media (${({
  theme
}) => theme.underScreenMedium}) {
    padding: 0 12px 12px;
  }
`;
const ConnectionsContainer = styled.div`
  margin-top: 5px;
  display: flex;
  flex-wrap: wrap;
  column-gap: 2%;
  & > div:not(#show-more-results) {
    flex: 48%;
    @media (${({
  theme
}) => theme.overScreenSmall}) {
      max-width: 49%;
    }
    & > div {
      margin: 14px 0;
      padding: 15px 15px 10px;
    }
    @media (${({
  theme
}) => theme.underScreenSmall}) {
      flex: 100%;
      & > div {
        margin: 5px auto;
        padding: 15px 15px 5px;
      }
    }
  }
`;
const StyledDropDown = styled(ButtonDropdown)`
  & > button {
    padding: 0;
  }
`;